import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import classnames from 'classnames';

import styles from './SearchInput.module.scss';
import { Form, Input, Spin } from 'antd';
import { Icon, IconImage, IconImageType } from 'src/modules/global/components/icon/Icon';
import { FormInstance } from 'antd/lib/form';
import { useIntl } from 'react-intl';
import useOnclickOutside from 'react-cool-onclickoutside';
import _ from 'lodash';

export interface SearchInputProps {
  name: string;
  form: FormInstance;
  placeholderKey: string;
  className?: string;
  onChange?: (newValue: string) => void;
  onFocus?: () => void;
  onDelete?: () => void;
  suggestions?: any[];
  children?: (suggestionProps: {
    suggestionsVisible: boolean;
    closeSuggestions: () => void;
    selectedSuggestion: any;
    selectedSuggestionIndex: number | null;
  }) => React.ReactElement;
  leftIcon?: IconImageType;
  leftIconLoading?: boolean;
  onLeftIconClick?: () => void;
  onPressEnter?: (e: React.KeyboardEventHandler<HTMLInputElement>) => void;
  getValueFromSuggestion?: (suggestion: any) => string;
  /**
   * Whether to trigger the search when the delete button is pressed
   */
  searchOnDelete?: boolean;
  /**
   * Callback triggered when suggestions need updating
   */
  updateSuggestions?: (searchTerm: string) => void;
  /**
   * Whether to enforce submitting a suggestion value from the suggestions list
   */
  enforceSuggestionValue?: boolean;
  /**
   * Event handler that is executed when a suggestion is selected by the user by keyboard
   */
  onSuggestionSelectedByEnterPress?: (
    selectedSuggestion: any,
    selectedSuggestionIndex: number
  ) => void;
}

const KEY_CODE_DOWN_ARROW = 40;
const KEY_CODE_UP_ARROW = 38;

export const SearchInput = ({
  name,
  className,
  form,
  children,
  suggestions,
  placeholderKey,
  searchOnDelete = false,
  leftIcon,
  leftIconLoading = false,
  enforceSuggestionValue = false,
  getValueFromSuggestion,
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  onPressEnter = () => {},
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  onLeftIconClick = () => {},
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  onChange,
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  onDelete = () => {},
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  onFocus = () => {},
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  updateSuggestions = () => {},
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  onSuggestionSelectedByEnterPress = () => {},
}: SearchInputProps) => {
  const t = useIntl();
  const [isOpen, setIsOpen] = useState(false);
  const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState<number>(null);
  const suggestionsLength = suggestions ? suggestions.length : 0;
  const inputRef = useRef(null);

  const value = form.getFieldValue(name);
  // reset selectedSuggestionIndex to null when suggestions are updated
  useEffect(() => {
    setSelectedSuggestionIndex(null);
  }, [suggestions]);

  const handleSearch = useCallback(() => {
    setIsOpen(false);
    form.submit();
  }, [form]);

  const handleValueChange = useCallback(
    (newValue) => {
      form.setFieldsValue({
        [name]: newValue,
      });

      setIsOpen(!_.isEmpty(newValue));

      updateSuggestions(newValue);
      if (onChange) {
        onChange(newValue);
      }
    },
    [form, name, onChange, updateSuggestions]
  );

  const handleValueDelete = useCallback(() => {
    form.resetFields([name]);
    handleValueChange('');
    onDelete();

    inputRef.current.focus();
    if (searchOnDelete) {
      // TODO: this could be an update bug.
      //  We're waiting for the component and its hoc to update their states
      setTimeout(() => handleSearch(), 1);
    }
  }, [form, handleSearch, handleValueChange, name, onDelete, searchOnDelete]);

  const handleFocus = useCallback(() => {
    if (value?.length) {
      setIsOpen(true);
    }
    onFocus();
  }, [onFocus, value?.length]);

  const closeSuggestions = useCallback(() => setIsOpen(false), [setIsOpen]);

  const selectedSuggestion = useMemo(() => {
    if (selectedSuggestionIndex !== null && suggestions[selectedSuggestionIndex]) {
      return suggestions[selectedSuggestionIndex];
    }
    return null;
  }, [selectedSuggestionIndex, suggestions]);

  const handlePressEnter = useCallback(
    (e) => {
      closeSuggestions();

      if (suggestionsLength) {
        if (!getValueFromSuggestion) {
          throw new Error(
            "Can't get value from suggestion. Prop getValueFromSuggestion must be set"
          );
        }
        if (selectedSuggestion) {
          handleValueChange(getValueFromSuggestion(selectedSuggestion));
          closeSuggestions();
          onSuggestionSelectedByEnterPress(selectedSuggestion, selectedSuggestionIndex);
        } else if (enforceSuggestionValue) {
          handleValueChange(getValueFromSuggestion(suggestions[0]));
          closeSuggestions();
          setSelectedSuggestionIndex(0);
          onSuggestionSelectedByEnterPress(suggestions[0], 0);
        }
      }

      form.submit();
      onPressEnter(e);
      inputRef.current.blur();
    },
    [
      closeSuggestions,
      enforceSuggestionValue,
      form,
      getValueFromSuggestion,
      handleValueChange,
      onPressEnter,
      onSuggestionSelectedByEnterPress,
      selectedSuggestion,
      selectedSuggestionIndex,
      suggestions,
      suggestionsLength,
    ]
  );

  const moveSelectionUp = useCallback(() => {
    if (suggestionsLength) {
      if (selectedSuggestionIndex === 0) {
        setSelectedSuggestionIndex(null);
      } else if (selectedSuggestionIndex === null) {
        setSelectedSuggestionIndex(suggestionsLength - 1);
      }
      setSelectedSuggestionIndex(selectedSuggestionIndex - 1);
    }
  }, [selectedSuggestionIndex, suggestionsLength]);

  const moveSelectionDown = useCallback(() => {
    if (suggestionsLength) {
      if (selectedSuggestionIndex === suggestions[suggestionsLength - 1]) {
        inputRef.current.focus();
        setSelectedSuggestionIndex(null);
      } else if (selectedSuggestionIndex === null) {
        setSelectedSuggestionIndex(0);
      } else {
        setSelectedSuggestionIndex(selectedSuggestionIndex + 1);
      }
    }
  }, [selectedSuggestionIndex, suggestions, suggestionsLength]);

  const handleKeyDown = useCallback(
    (e) => {
      if (suggestionsLength && isOpen) {
        switch (e.keyCode) {
          case KEY_CODE_DOWN_ARROW:
            moveSelectionDown();
            e.preventDefault();
            e.stopPropagation();
            break;

          case KEY_CODE_UP_ARROW:
            moveSelectionUp();
            e.preventDefault();
            e.stopPropagation();
            break;
        }
      }
    },
    [suggestionsLength, isOpen, moveSelectionDown, moveSelectionUp]
  );

  const clickOutsideRef = useOnclickOutside(() => {
    setIsOpen(false);
  });

  const hasSearchSuggestions = suggestions && !!suggestionsLength;

  const searchSuggestions = useMemo(() => {
    if (!children) {
      return false;
    }

    const suggestionsVisible = isOpen && hasSearchSuggestions;

    const searchSuggestionsElement = children({
      suggestionsVisible,
      closeSuggestions,
      selectedSuggestionIndex,
      selectedSuggestion,
    });

    return React.cloneElement(searchSuggestionsElement, {
      className: classnames(
        searchSuggestionsElement.props.className,
        styles.searchSuggestionsContainer,
        !suggestionsVisible && styles.searchSuggestionsHidden
      ),
    });
  }, [
    children,
    closeSuggestions,
    hasSearchSuggestions,
    isOpen,
    selectedSuggestion,
    selectedSuggestionIndex,
  ]);

  return (
    <div ref={clickOutsideRef} className={classnames(styles.container, className)}>
      <div className={styles.searchContainer}>
        <Form.Item name={name} className={styles.formItem}>
          <Input
            ref={inputRef}
            className={classnames(
              styles.searchInput,
              value?.length && styles.withText,
              !!leftIcon && styles.withLeftIcon
            )}
            type="text"
            {...{ autoComplete: 'off' }}
            onPressEnter={handlePressEnter}
            onKeyDown={handleKeyDown}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleValueChange(e.target.value)}
            onFocus={handleFocus}
            onSubmit={handleSearch}
            placeholder={t.formatMessage({
              id: placeholderKey,
            })}
            value={value}
          />
        </Form.Item>
        {leftIcon && (
          <div
            className={classnames(
              styles.iconContainer,
              styles.leftIconContainer,
              leftIconLoading && styles.leftIconLoading
            )}
            onClick={!leftIconLoading ? onLeftIconClick : null}>
            {leftIconLoading ? <Spin size="small" /> : <Icon image={leftIcon} />}
          </div>
        )}
        {!!value?.length && (
          <div
            className={classnames(styles.iconContainer, styles.deleteIconContainer)}
            onClick={handleValueDelete}>
            <Icon image={IconImage.Close} />
          </div>
        )}
        <div
          className={classnames(styles.iconContainer, styles.searchIconContainer)}
          onClick={handleSearch}>
          <Icon image={IconImage.Search} className={styles.searchIcon} />
        </div>
      </div>
      {searchSuggestions}
    </div>
  );
};
